// TODO: From App New
<template>
  <div class="row">
    <!-- Wizard Navigation -->
    <SaleWizardNavigation :sale-id="$route.params.id" />

    <!-- Main Content -->
    <div class="col">
      <div class="row">
        <!-- Left Column - Selected Brands -->
        <div class="col-md-6">
          <SaleBrandsSelected
            :selected-brands="selectedBrands"
            :loading="loadingSelectedBrands"
            @setup-price="setupPrice"
            @share-url="shareUrl"
            @remove-brand="removeBrand"
          />
        </div>

        <!-- Right Column - All Brands -->
        <div class="col-md-6">
          <SaleBrandsAll
            :all-brands="allBrands"
            :selected-brands="selectedBrands"
            :loading="loading"
            @toggle-brand="toggleBrand"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import SaleWizardNavigation from '@/components/sale/SaleWizardNavigation.vue'
import SaleBrandsSelected from '@/components/sale/SaleBrandsSelected.vue'
import SaleBrandsAll from '@/components/sale/SaleBrandsAll.vue'

export default {
  name: 'SaleBrands',
  components: {
    SaleWizardNavigation,
    SaleBrandsSelected,
    SaleBrandsAll,
  },
  data() {
    return {
      // No local data needed - child components manage their own state
    }
  },
  computed: {
    allBrands() {
      return this.$store.state.saleV2.merchantsData
    },
    loading() {
      return this.$store.state.saleV2.loadingMerchants
    },
    selectedBrands() {
      return this.$store.state.saleV2.saleBrandsData
    },
    loadingSelectedBrands() {
      return this.$store.state.saleV2.loadingSaleBrands
    },
    userData() {
      return this.$store.state.auth.userData
    },
    isMerchant() {
      return this.userData?.role === 'merchant'
    },
  },
  mounted() {
    // Redirect merchant to setup harga if they try to access brands page
    if (this.isMerchant && this.userData?.merchant?.slug) {
      this.$router.push(`/sale/${this.$route.params.id}/brand/${this.userData.merchant.slug}/price`)
      return
    }

    // Load sale detail if not already loaded
    if (!this.$store.state.saleV2.detailData) {
      this.$store.dispatch('saleV2/getDetail', this.$route.params.id)
    }
    // Load merchants from API only if not already loaded
    this.loadMerchantsIfNeeded()
    // Load selected brands from API
    this.loadSaleBrands()
  },
  methods: {
    toggleBrand(brand) {
      const isSelected = this.isBrandSelected(brand)

      if (isSelected) {
        // Remove brand from promo via API
        this.removeBrandFromPromo(brand)
      } else {
        // Add brand to promo via API
        this.addBrandToPromo(brand)
      }
    },
    isBrandSelected(brand) {
      return this.selectedBrands.some(selected => selected.id === brand.id)
    },
    setupPrice(brand) {
      // Navigate to setup page with sale ID and brand slug
      this.$router.push({
        name: 'sale-setup',
        params: {
          id: this.$route.params.id,
          brandSlug: brand.slug,
        },
      })
    },
    shareUrl(brand) {
      // Generate setup URL with sale ID and brand slug
      // Use parent URL if embedded, otherwise use current origin
      const isEmbedded = this.$route.query.embedded === 'true'
      const baseUrl = isEmbedded ? process.env.VUE_APP_PARENT_URL : window.location.origin
      const saleUrl = `${baseUrl}/sale/${this.$route.params.id}/brand/${brand.slug}/price`

      // Copy to clipboard (if supported)
      if (navigator.clipboard) {
        navigator.clipboard.writeText(saleUrl).then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Sukses',
              icon: 'CopyIcon',
              text: `URL ${brand.name} berhasil disalin ke clipboard`,
              variant: 'success',
            },
          })
        })
      } else {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Share URL',
            icon: 'ShareIcon',
            text: `URL: ${saleUrl}`,
            variant: 'info',
          },
        })
      }
    },
    removeBrand(brand) {
      // Remove brand from promo via API
      this.removeBrandFromPromo(brand)
    },
    loadMerchantsIfNeeded() {
      // Only load if merchants data is empty and not currently loading
      if (this.allBrands.length === 0 && !this.loading) {
        this.loadMerchants()
      }
    },
    loadMerchants() {
      const params = {
        limit: 1000,
        page: 1,
        // Add search parameter if needed
        ...(this.searchKeyword && { keyword: this.searchKeyword }),
      }

      return this.$store.dispatch('saleV2/getMerchants', params)
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              icon: 'AlertCircleIcon',
              text: 'Gagal memuat daftar merchants',
              variant: 'danger',
            },
          })
        })
    },
    loadSaleBrands() {
      return this.$store.dispatch('saleV2/getSaleBrands', this.$route.params.id)
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              icon: 'AlertCircleIcon',
              text: 'Gagal memuat daftar brand terpilih',
              variant: 'danger',
            },
          })
        })
    },
    addBrandToPromo(brand) {
      const merchantIds = [brand.id]

      // Optimistic update - add brand to UI first
      const currentBrands = [...this.$store.state.saleV2.saleBrandsData]
      this.$store.commit('saleV2/SET_SALE_BRANDS_DATA', [...currentBrands, brand])

      // Then call API
      this.$store.dispatch('saleV2/addSaleBrands', {
        id: this.$route.params.id,
        merchant_ids: merchantIds,
      })
        .then(() => {
          // API success - brand already added optimistically, just show success toast
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Sukses',
              icon: 'CheckIcon',
              text: `${brand.name} berhasil ditambahkan ke promo`,
              variant: 'success',
            },
          })

          // Keep the optimistic update, no need to rollback or update again
        })
        .catch(error => {
          // Rollback - return to previous state
          this.$store.commit('saleV2/SET_SALE_BRANDS_DATA', currentBrands)

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              icon: 'AlertCircleIcon',
              text: error.message || 'Gagal menambahkan brand ke promo',
              variant: 'danger',
            },
          })
        })
    },
    removeBrandFromPromo(brand) {
      const merchantIds = [brand.id]

      // Optimistic update - remove brand from UI first
      const currentBrands = [...this.$store.state.saleV2.saleBrandsData]
      const updatedBrands = currentBrands.filter(b => b.id !== brand.id)
      this.$store.commit('saleV2/SET_SALE_BRANDS_DATA', updatedBrands)

      // Then call API
      this.$store.dispatch('saleV2/removeSaleBrands', {
        id: this.$route.params.id,
        merchant_ids: merchantIds,
      })
        .then(() => {
          // API success - brand already removed optimistically, just show success toast
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Sukses',
              icon: 'CheckIcon',
              text: `${brand.name} berhasil dihapus dari promo`,
              variant: 'warning',
            },
          })

          // Keep the optimistic update, no need to rollback or update again
        })
        .catch(error => {
          // Rollback - return to previous state
          this.$store.commit('saleV2/SET_SALE_BRANDS_DATA', currentBrands)

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              icon: 'AlertCircleIcon',
              text: error.message || 'Gagal menghapus brand dari promo',
              variant: 'danger',
            },
          })
        })
    },
  },
}
</script>

<style scoped>
/* Main container styles only - component-specific styles moved to child components */
</style>
